import { esc, html } from './ui';

/**
 * Small charts, drawn as SVG.
 *
 * These exist to show a direction, not a value — a single oil consumption
 * figure means nothing, twelve months of them means a cylinder is wearing. So
 * every chart here carries its threshold line: a reading is only high or low
 * relative to the limit it is judged against.
 */

export interface TrendPoint {
  label: string;
  value: number;
}

/** A line against a threshold. Used for consumption and rate trends. */
export function trendChart(points: TrendPoint[], limit: number, unit: string, ariaLabel: string): string {
  if (points.length < 2) {
    return html`<p class="muted tiny">Not enough history yet to show a trend.</p>`;
  }

  const W = 560, H = 150, padL = 34, padB = 18, padT = 14, padR = 44;
  const values = points.map((p) => p.value);
  const max = Math.max(Math.max(...values), limit) * 1.18 || 1;
  const iw = W - padL - padR, ih = H - padB - padT;
  const X = (i: number) => padL + i * (iw / (points.length - 1));
  const Y = (v: number) => padT + ih - (v / max) * ih;

  // A grid line roughly every quarter of the range, rounded to something a
  // person would actually write down.
  const step = niceStep(max / 4);
  let grid = '', labs = '';
  for (let g = 0; g <= max; g += step) {
    const y = Y(g);
    grid += `<line class="gridline" x1="${padL}" y1="${y.toFixed(1)}" x2="${W - padR}" y2="${y.toFixed(1)}"/>`;
    labs += `<text class="axl" x="${padL - 6}" y="${(y + 3).toFixed(1)}" text-anchor="end">${g.toFixed(2)}</text>`;
  }

  const line = points.map((p, i) => `${i ? 'L' : 'M'}${X(i).toFixed(1)},${Y(p.value).toFixed(1)}`).join(' ');
  const area = `${line} L${X(points.length - 1).toFixed(1)},${Y(0).toFixed(1)} L${X(0).toFixed(1)},${Y(0).toFixed(1)} Z`;
  const dots = points
    .map(
      (p, i) =>
        `<circle class="trend-dot${p.value >= limit ? ' hot' : ''}" cx="${X(i).toFixed(1)}" cy="${Y(p.value).toFixed(1)}" r="${
          i === points.length - 1 ? 4.5 : 3
        }" data-m="${esc(p.label)}" data-v="${p.value.toFixed(2)}"/>`,
    )
    .join('');

  // Every other label, so they do not collide on a narrow screen.
  const everyOther = points.length > 8 ? 2 : 1;
  points.forEach((p, i) => {
    if (i % everyOther !== 0 && i !== points.length - 1) return;
    labs += `<text class="axl" x="${X(i).toFixed(1)}" y="${H - 4}" text-anchor="middle">${esc(p.label)}</text>`;
  });

  const ly = Y(limit);

  return html`<div class="chart" data-unit=" ${esc(unit)}">
    <svg viewBox="0 0 ${W} ${H}" role="img" aria-label="${esc(ariaLabel)}">
      ${grid}
      <path class="trend-area" d="${area}"/>
      <path class="trend-line" d="${line}"/>
      <line class="thresh" x1="${padL}" y1="${ly.toFixed(1)}" x2="${W - padR}" y2="${ly.toFixed(1)}"/>
      <text class="axl" x="${W - padR + 5}" y="${(ly + 3).toFixed(1)}">limit ${limit.toFixed(2)}</text>
      ${dots}
      ${labs}
    </svg>
    <span class="tip" hidden></span>
  </div>`;
}

/** A bare line, for a row in a list. No axes — it is only shape. */
export function sparkline(values: number[], w = 120, h = 30, lo?: number, hi?: number): string {
  if (values.length < 2) return '';
  const min = lo ?? Math.min(...values);
  const max = hi ?? Math.max(...values);
  const range = max - min || 1;
  const pts = values
    .map((v, i) => `${(i * (w / (values.length - 1))).toFixed(1)},${(h - 2 - ((v - min) / range) * (h - 4)).toFixed(1)}`)
    .join(' ');
  return `<svg viewBox="0 0 ${w} ${h}" preserveAspectRatio="none" aria-hidden="true"><polyline class="sl" points="${pts}"/></svg>`;
}

/** Monthly bars — hours flown, defects raised. */
export function barChart(points: TrendPoint[], unit: string, ariaLabel: string): string {
  if (!points.length) return html`<p class="muted tiny">Nothing recorded in this period.</p>`;

  const W = 560, H = 140, padL = 34, padB = 18, padT = 12, padR = 10;
  const max = Math.max(...points.map((p) => p.value)) * 1.15 || 1;
  const iw = W - padL - padR, ih = H - padB - padT;
  const bw = Math.max(4, (iw / points.length) * 0.62);

  const bars = points
    .map((p, i) => {
      const x = padL + i * (iw / points.length) + (iw / points.length - bw) / 2;
      const barH = (p.value / max) * ih;
      return `<rect class="bar" x="${x.toFixed(1)}" y="${(padT + ih - barH).toFixed(1)}" width="${bw.toFixed(1)}" height="${Math.max(
        0,
        barH,
      ).toFixed(1)}" rx="2" data-m="${esc(p.label)}" data-v="${p.value.toFixed(1)}"/>`;
    })
    .join('');

  const everyOther = points.length > 8 ? 2 : 1;
  const labs = points
    .map((p, i) =>
      i % everyOther !== 0 && i !== points.length - 1
        ? ''
        : `<text class="axl" x="${(padL + i * (iw / points.length) + iw / points.length / 2).toFixed(1)}" y="${H - 4}" text-anchor="middle">${esc(p.label)}</text>`,
    )
    .join('');

  return html`<div class="chart" data-unit=" ${esc(unit)}">
    <svg viewBox="0 0 ${W} ${H}" role="img" aria-label="${esc(ariaLabel)}">${bars}${labs}</svg>
    <span class="tip" hidden></span>
  </div>`;
}

/** Hover readout for any chart rendered above. */
export function bindCharts(root: HTMLElement): void {
  root.querySelectorAll<HTMLElement>('.chart').forEach((chart) => {
    const tip = chart.querySelector<HTMLElement>('.tip');
    if (!tip) return;
    const unit = chart.dataset.unit ?? '';
    chart.querySelectorAll<SVGElement>('.bar,.trend-dot').forEach((mark) => {
      mark.addEventListener('mouseenter', () => {
        const r = mark.getBoundingClientRect();
        const cr = chart.getBoundingClientRect();
        tip.textContent = `${mark.getAttribute('data-m')} · ${mark.getAttribute('data-v')}${unit}`;
        tip.style.left = `${r.left - cr.left + r.width / 2}px`;
        tip.style.top = `${r.top - cr.top - 8}px`;
        tip.hidden = false;
      });
      mark.addEventListener('mouseleave', () => {
        tip.hidden = true;
      });
    });
  });
}

/** 0.23 → 0.25, 3.7 → 5, so the axis reads in round numbers. */
function niceStep(raw: number): number {
  const pow = Math.pow(10, Math.floor(Math.log10(raw || 1)));
  const n = raw / pow;
  const snapped = n <= 1 ? 1 : n <= 2 ? 2 : n <= 2.5 ? 2.5 : n <= 5 ? 5 : 10;
  return snapped * pow;
}
